import { useCallback, useEffect, useState } from "react";
import { Loader2, Sparkles } from "lucide-react";

import {
  COACHING_AUTONOMY_MODES,
  fetchCoachingSettings,
  updateCoachingSettings,
} from "@/api/coachingSettingsApi";
import { Button } from "@/components/ui/button";
import { SectionHeader } from "@/components/layout/SectionHeader";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const OPTIONS = [
  {
    id: COACHING_AUTONOMY_MODES.RECOMMENDATIONS_ONLY,
    title: "Recommendations only",
    description:
      "Apex can recommend training changes, but you approve every change.",
  },
  {
    id: COACHING_AUTONOMY_MODES.SAFE_RECOVERY_AUTO,
    title: "Safe recovery auto",
    description:
      "Apex may automatically make small recovery-focused adjustments when training data strongly indicates more recovery is needed.",
    footnote:
      "Major schedule, availability, FTP, key-workout and broader plan changes will still require your approval.",
  },
];

export const CoachingAutonomySettings = ({ testId = "coaching-autonomy-settings" }) => {
  const [mode, setMode] = useState(COACHING_AUTONOMY_MODES.RECOMMENDATIONS_ONLY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingMode, setPendingMode] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchCoachingSettings();
      setMode(data.coaching_autonomy_mode || COACHING_AUTONOMY_MODES.RECOMMENDATIONS_ONLY);
    } catch {
      setError("We couldn't load your AI coaching adjustment settings.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const persistMode = async (nextMode) => {
    setSaving(true);
    setError(null);
    try {
      const data = await updateCoachingSettings(nextMode);
      setMode(data.coaching_autonomy_mode || nextMode);
    } catch {
      setError("We couldn't save your AI coaching adjustment settings.");
    } finally {
      setSaving(false);
      setConfirmOpen(false);
      setPendingMode(null);
    }
  };

  const handleSelect = (nextMode) => {
    if (nextMode === mode || saving) return;
    if (
      nextMode === COACHING_AUTONOMY_MODES.SAFE_RECOVERY_AUTO
      && mode === COACHING_AUTONOMY_MODES.RECOMMENDATIONS_ONLY
    ) {
      setPendingMode(nextMode);
      setConfirmOpen(true);
      return;
    }
    persistMode(nextMode);
  };

  return (
    <section data-testid={testId} className="app-card-elevated p-5 sm:p-6">
      <SectionHeader
        icon={Sparkles}
        title="AI coaching adjustments"
        description="Choose how much authority Apex has when proactively reviewing your training plan."
      />

      {loading ? (
        <p className="mt-4 text-sm text-dark-text-secondary flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
          Loading settings…
        </p>
      ) : (
        <div className="mt-4 space-y-3">
          {OPTIONS.map((option) => {
            const selected = mode === option.id;
            return (
              <button
                key={option.id}
                type="button"
                disabled={saving}
                data-testid={`${testId}-option-${option.id}`}
                data-selected={selected ? "true" : "false"}
                onClick={() => handleSelect(option.id)}
                className={`w-full text-left rounded-lg border px-4 py-3 transition-colors ${
                  selected
                    ? "border-brand-primary/50 bg-brand-primary/10"
                    : "border-dark-border hover:border-dark-border/80"
                }`}
              >
                <p className="text-sm font-medium text-dark-text-primary">{option.title}</p>
                <p className="mt-1 text-sm text-dark-text-secondary">{option.description}</p>
                {option.footnote && (
                  <p className="mt-2 text-xs text-dark-text-muted">{option.footnote}</p>
                )}
              </button>
            );
          })}
        </div>
      )}

      {error && (
        <p className="mt-3 text-sm text-analytics-amber" role="alert">{error}</p>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Enable Safe Recovery Auto?</AlertDialogTitle>
            <AlertDialogDescription>
              Apex will be allowed to automatically make limited recovery-focused
              changes when confidence is high. Other training-plan changes will
              still require your approval.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={saving}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={saving}
              data-testid={`${testId}-confirm-safe-auto`}
              onClick={() => pendingMode && persistMode(pendingMode)}
            >
              Enable Safe Recovery Auto
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
};
